/**
 * selectedColumnKeys — which picker columns a clause (SELECT list, ORDER BY,
 * GROUP BY) already contains, so the column pickers can check-mark them and
 * turn a pick on a checked row into a remove.
 *
 * Only plain column references count (`name`, `c.name`, `schema.table.name`,
 * quoted parts included); expressions, function calls, `*` and ordinals are
 * never matched. A qualified reference resolves through the statement's
 * FROM / JOIN aliases (or table names); a bare one only when exactly one
 * in-query table has a column of that name — an ambiguous bare name marks
 * nothing rather than guessing.
 *
 * Relations are compared by identity (`sameRelation`: name, and schema when
 * both sides carry one), so pass the statement's relations resolved to their
 * snapshot relations and a mark never lands on another schema's same-named
 * table.
 *
 * Returns the picker key (`pickerKeysFor(columns).columnKey`) → the index of the FIRST
 * clause item referencing it (the one a remove targets).
 */

import type { QualifiedName } from '../../../../sql-grammar/model';
import { relationKey, sameRelation } from '../../../../sql-resolver/service/snapshotRelations';
import type { Column } from '../../../../sql-resolver/types/schema';
import type { InQueryRelation } from '../../edit/edits/joinHelpers';
import { pickerKeysFor } from './columnGroups';

const PLAIN_PART = /^[A-Za-z_][\w$#]*$/;
const CLOSERS: Record<string, string> = { '"': '"', '[': ']', '`': '`' };

/** One part of a dotted reference: its text with delimiters stripped, and whether it was delimited. */
export type ColumnRefPart = { readonly value: string; readonly quoted: boolean };

/** Split `a.b."c d"` into unquoted parts; null when it isn't a plain dotted reference. */
export function splitColumnRef(expression: string): string[] | null {
  return splitQuotedColumnRef(expression)?.map((p) => p.value) ?? null;
}

/** {@link splitColumnRef}, keeping whether each part was delimited (engines that fold bare names need it). */
export function splitQuotedColumnRef(expression: string): ColumnRefPart[] | null {
  const text = expression.trim();
  if (text.length === 0) return null;
  const parts: ColumnRefPart[] = [];
  let i = 0;
  while (i < text.length) {
    const open = text[i]!;
    const close = CLOSERS[open];
    if (close) {
      let j = i + 1;
      let value = '';
      while (j < text.length) {
        if (text[j] === close) {
          // `""` inside a double-quoted identifier is an escaped quote.
          if (close === '"' && text[j + 1] === '"') {
            value += '"';
            j += 2;
            continue;
          }
          break;
        }
        value += text[j];
        j++;
      }
      if (j >= text.length || value.length === 0) return null;
      parts.push({ value, quoted: true });
      i = j + 1;
    } else {
      let j = i;
      while (j < text.length && text[j] !== '.') j++;
      const value = text.slice(i, j).trim();
      if (!PLAIN_PART.test(value)) return null;
      parts.push({ value, quoted: false });
      i = j;
    }
    if (i < text.length) {
      if (text[i] !== '.') return null;
      i++;
      if (i >= text.length) return null;
    }
  }
  return parts;
}

export function selectedColumnKeys(
  expressions: readonly string[],
  relations: readonly InQueryRelation[],
  columns: readonly Column[],
): Map<string, number> {
  const out = new Map<string, number>();
  const keyOf = pickerKeysFor(columns).columnKey;
  const inQuery = (c: Column): boolean => relations.some((r) => sameRelation(r.qualified, c.relation));
  const inQueryColumns = relations.length > 0 ? columns.filter(inQuery) : columns;

  expressions.forEach((expression, index) => {
    const parts = splitColumnRef(expression);
    if (!parts) return;
    const name = parts[parts.length - 1]!.toLowerCase();
    const qualifier = parts.length >= 2 ? parts[parts.length - 2]!.toLowerCase() : null;

    let owners: QualifiedName[];
    if (qualifier !== null) {
      owners =
        relations.length > 0
          ? relations
              .filter(
                (r) => r.alias?.toLowerCase() === qualifier || r.qualified.name.toLowerCase() === qualifier,
              )
              .map((r) => r.qualified)
          : columns.filter((c) => c.relation.name.toLowerCase() === qualifier).map((c) => c.relation);
    } else {
      owners = inQueryColumns
        .filter((c) => c.qualified.name.toLowerCase() === name)
        .map((c) => c.relation);
    }
    const distinctOwners = [...new Map(owners.map((o) => [relationKey(o), o])).values()];
    if (distinctOwners.length !== 1) return;
    const matches = columns.filter(
      (c) => sameRelation(distinctOwners[0], c.relation) && c.qualified.name.toLowerCase() === name,
    );
    // An owner that names no single relation (a bare name two schemas share) marks nothing.
    if (new Set(matches.map((c) => relationKey(c.relation))).size !== 1) return;
    const column = matches[0];
    const key = keyOf(column);
    if (!out.has(key)) out.set(key, index);
  });
  return out;
}
